import config from '@config';
import {
  type AgentReadModel,
  AgentsStreamError,
  type ConversationListReadModel,
  type CreateConversationResult,
  type StreamMessage,
  initializeAgentsApi,
} from '@gasbi/shared/common/features/agents';
import type { InitializeApiProps } from '@gasbi/shared/common/features/types';
import OrganizationManager from '@gasbi/shared/common/utils/organizationManager';

import { reportUnauthorized } from '@/features/agents/unauthorized';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

// Every list and mutation of the slice reports a 401: the pages check the session again then (ADR-0004 §2.3).
const baseQuery: InitializeApiProps['baseQuery'] = options => {
  const query = axiosBaseQuery(options);
  return async (args, api, extraOptions) => {
    const result = await query(args, api, extraOptions);
    if (result.error?.status === 401) reportUnauthorized();
    return result;
  };
};

const shared = initializeAgentsApi({
  baseUrl: config.VITE_AGENTS_API_URL,
  baseQuery,
  // The organization the host app chose (the panel sets it from the launcher); axios adds it from the same manager.
  getStreamHeaders: (): Record<string, string> => {
    const organizationId = OrganizationManager.getState();
    return organizationId ? { organizationId } : {};
  },
});

export type ChatConversationsQuery = {
  /** The panel's app: only the conversations started in it; none = all of the user's chats (ADR-0004 §2.8). */
  app?: string;
  page: number;
  pageSize: number;
};

export type CreateChatConversationRequest = {
  agentId: string;
  /** The panel's app; a new conversation of the full page has none. */
  appKey?: string;
};

/**
 * The app's instance of the shared agents slice. The shared endpoints know no app yet, so the three that take one
 * (ADR-0004 §2.8) are added here: the agents and the conversations of an app, and a conversation started in an app.
 * They share the slice's cache tags, so a finished turn refreshes them as it refreshes the shared ones.
 */
export const agentsApi = shared.agentsApi.injectEndpoints({
  endpoints: builder => ({
    getChatAgents: builder.query<AgentReadModel[], string | undefined>({
      query: app => ({ url: '/', params: app ? { app } : {} }),
      providesTags: ['Agents'],
    }),
    getChatConversations: builder.query<
      ConversationListReadModel,
      ChatConversationsQuery
    >({
      query: ({ app, ...paging }) => ({
        url: '/conversations',
        params: app ? { ...paging, app } : paging,
      }),
      providesTags: ['Conversations'],
    }),
    createChatConversation: builder.mutation<
      CreateConversationResult,
      CreateChatConversationRequest
    >({
      // `appKey` only with a real key: the service refuses an unknown one (Agents.UnknownChatApp).
      query: ({ agentId, appKey }) => ({
        url: '/conversations',
        method: 'POST',
        data: appKey ? { agentId, appKey } : { agentId },
      }),
      invalidatesTags: ['Conversations'],
    }),
  }),
});

export const {
  useGetChatAgentsQuery,
  useGetChatConversationsQuery,
  useCreateChatConversationMutation,
} = agentsApi;

export const { useGetConversationQuery } = shared;

/** The shared stream, with a 401 before the first event reported like the slice's (the cookie session has ended). */
export const streamMessage: StreamMessage = async function* (request, options) {
  try {
    yield* shared.streamMessage(request, options);
  } catch (error) {
    if (error instanceof AgentsStreamError && error.status === 401)
      reportUnauthorized();
    throw error;
  }
};
